import React from "react"
import {Link} from "react-router-dom";

import globalStyles from "../../../../App.module.scss";

const permissionsCommands = `mkdir -p ~/.ssh
chmod 700 ~/.ssh
touch ~/.ssh/config
chmod 600 ~/.ssh/config
nano ~/.ssh/config`;

const minimalConfig = `Host web
  HostName web.example.com
  User YOUR_USERNAME
  IdentityFile ~/.ssh/id_ed25519_web
  IdentitiesOnly yes`;

const gitIdentitiesConfig = `Host git-personal
  HostName git.example.com
  User git
  IdentityFile ~/.ssh/id_ed25519_personal
  IdentitiesOnly yes

Host git-work
  HostName git.example.com
  User git
  IdentityFile ~/.ssh/id_ed25519_work
  IdentitiesOnly yes`;

const bastionConfig = `Host bastion
  HostName bastion.example.com
  User YOUR_BASTION_USERNAME
  IdentityFile ~/.ssh/id_ed25519_bastion
  IdentitiesOnly yes

Host private-app
  HostName app.internal.example
  User YOUR_APP_USERNAME
  IdentityFile ~/.ssh/id_ed25519_app
  IdentitiesOnly yes
  ProxyJump bastion`;

const defaultsConfig = `Include ~/.ssh/config.d/*.conf

Host production-web
  HostName web.example.com
  User deploy
  Port 2222

Host *
  ServerAliveInterval 30
  ServerAliveCountMax 3
  ConnectTimeout 10
  ForwardAgent no
  StrictHostKeyChecking ask`;

const tunnelConfig = `Host database-tunnel
  HostName bastion.example.com
  User YOUR_USERNAME
  IdentityFile ~/.ssh/id_ed25519_bastion
  IdentitiesOnly yes
  LocalForward 127.0.0.1:5432 database.internal.example:5432
  ExitOnForwardFailure yes`;

export default function SshClientConfig() {
    return (
        <div className={globalStyles.article}>
            <h1>SSH client configuration (~/.ssh/config)</h1>

            <p><code>~/.ssh/config</code> configures the OpenSSH <b>client</b> used by commands such as
                <code> ssh</code>, <code>scp</code>, and <code>sftp</code>. It does not configure the remote
                SSH server; server-side settings belong in <code>sshd_config</code>. For creating keys and
                installing a public key on a server, see <Link to="/pre-shared-keys">SSH Key Auth</Link>.</p>

            <h2>Create the user configuration safely</h2>
            <p>Run these commands as your normal user. The directory should be owned by you and mode 700; the
                client configuration should be owned by you and mode 600. If either was previously created by
                root, repair its ownership with <code>sudo chown "$USER":"$(id -gn)" ~/.ssh ~/.ssh/config</code>.</p>
            <pre className={globalStyles.code}>{permissionsCommands}</pre>

            <h2>Start with one host alias</h2>
            <p>Replace <code>YOUR_USERNAME</code> and the example key path with your real local values. The alias in
                <code> Host</code> is what you type; <code>HostName</code> is the DNS name or address actually used.</p>
            <pre className={globalStyles.code}>{minimalConfig}</pre>
            <pre className={globalStyles.code}>ssh web</pre>

            <h2>How values are selected</h2>
            <p>For nearly every option, OpenSSH uses the <b>first value it obtains</b>: command-line options first,
                then <code>~/.ssh/config</code>, then system defaults in <code>/etc/ssh/ssh_config</code>. Within a
                file, put specific <code>Host</code> blocks before <code>Host *</code> so the wildcard only fills
                values that are still unset.</p>
            <p><code>IdentityFile</code> is an important exception: repeated entries are additive and all listed
                identities may be tried in sequence. Use <code>IdentitiesOnly yes</code> on hosts that must offer
                only configured identities rather than every key exposed by an agent.</p>

            <h2>Common options</h2>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr><th>Option</th><th>Purpose and safe default</th></tr>
                    </thead>
                    <tbody>
                    <tr><td><b>Host</b></td><td>Alias or pattern that starts a conditional block.</td></tr>
                    <tr><td><b>HostName</b></td><td>Real DNS name or IP address for the destination.</td></tr>
                    <tr><td><b>User</b></td><td>Remote account name.</td></tr>
                    <tr><td><b>Port</b></td><td>Remote SSH port; default is 22.</td></tr>
                    <tr><td><b>IdentityFile</b></td><td>Private key path. Multiple declarations add identities.</td></tr>
                    <tr><td><b>IdentitiesOnly</b></td><td>With <code>yes</code>, restrict authentication to configured identities instead of unrelated agent keys.</td></tr>
                    <tr><td><b>AddKeysToAgent</b></td><td>Add a successfully used private key to a running agent; optional values can control lifetime on supported versions.</td></tr>
                    <tr><td><b>ForwardAgent</b></td><td>Expose your agent through the remote host. Leave off or <code>no</code> unless a trusted, specific host requires it.</td></tr>
                    <tr><td><b>ServerAliveInterval</b></td><td>Seconds between encrypted keepalive probes while idle.</td></tr>
                    <tr><td><b>ServerAliveCountMax</b></td><td>Unanswered keepalives allowed before disconnecting.</td></tr>
                    <tr><td><b>ConnectTimeout</b></td><td>Seconds allowed for connection setup and initial SSH handshake.</td></tr>
                    <tr><td><b>StrictHostKeyChecking</b></td><td>Use <code>yes</code> for pre-provisioned keys or <code>ask</code> for interactive first use. Verify a first-use fingerprint through a trusted independent channel before accepting it; never hide changed-key warnings with <code>no</code>.</td></tr>
                    <tr><td><b>UserKnownHostsFile</b></td><td>Select a real known-hosts file when isolation is needed; do not point it at <code>/dev/null</code>.</td></tr>
                    <tr><td><b>ProxyJump</b></td><td>Reach a destination through a jump host. Give the jump host its own block and credentials.</td></tr>
                    <tr><td><b>Include</b></td><td>Read matching fragments at that exact point. Globs are processed in lexical order, so ordering affects which value is first.</td></tr>
                    </tbody>
                </table>
            </div>

            <h2>Templates</h2>

            <h3>Separate personal and work Git identities</h3>
            <p>The aliases select different keys even when both accounts use the same Git service. Use the alias in
                the Git remote, for example <code>git@git-work:YOUR_ORG/YOUR_REPOSITORY.git</code>.</p>
            <pre className={globalStyles.code}>{gitIdentitiesConfig}</pre>

            <h3>Bastion with separate credentials</h3>
            <p><code>ProxyJump</code> applies destination settings to the destination, not automatically to the jump
                host, so each host has its own account and key.</p>
            <pre className={globalStyles.code}>{bastionConfig}</pre>
            <pre className={globalStyles.code}>ssh private-app</pre>

            <h3>Global defaults plus per-host overrides</h3>
            <p>A leading <code>Include</code> reads host-specific fragments first. Keep the broad defaults last.
                Included files are supported by modern OpenSSH; check <code>man ssh_config</code> on older clients.</p>
            <pre className={globalStyles.code}>{defaultsConfig}</pre>

            <h3>Optional loopback-only local tunnel</h3>
            <p>This binds only to local loopback. <code>ExitOnForwardFailure</code> detects failure to establish the
                forwarding listener, but not a later failure to reach the final database service. See
                <Link to="/ssh-tunneling"> SSH tunneling</Link> for forwarding concepts.</p>
            <pre className={globalStyles.code}>{tunnelConfig}</pre>
            <pre className={globalStyles.code}>ssh -N database-tunnel</pre>

            <h2>Inspect and troubleshoot</h2>
            <pre className={globalStyles.code}>{`ssh -G web
ssh -v web`}</pre>
            <p><code>ssh -G</code> prints the fully evaluated configuration and exits without making an SSH
                connection. <code>ssh -v</code> attempts a connection with diagnostics; its output can expose user
                names, host names, addresses, and local paths, so redact it before sharing.</p>

            <p>Reference: the upstream OpenSSH <a href="https://man.openbsd.org/ssh_config">ssh_config(5)</a> and
                <a href="https://man.openbsd.org/ssh"> ssh(1)</a> manuals.</p>
        </div>
    )
}
